import * as React from "react";

import { format } from "lib/globalize-util";
import { IconButton, Menu, MenuItem } from "@material-ui/core";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import { makeStyles } from "@material-ui/styles";
import classnames from "classnames";

import me from "models/me";
import { themeOptions } from "styles/theme";

import ArchiveProductConfirmation from "./archive-product-confirmation";
import CancelProductConfirmation from "./cancel-product-confirmation";

const useStyles = makeStyles({
    container: {
        position: "absolute",
        top: "4px",
        right: "40px",
        transition: "all .2s ease-in-out",
        borderRadius: "50%",
        background: "rgba(255, 255, 255, 0.7)",
        height: "32px",
        width: "32px",
    },
    contextTrigger: {
        width: 32,
        height: 32,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
});

enum ProductActions {
    Cancel = "Cancel",
    Archive = "Archive",
}
interface IContextOption {
    key: ProductActions;
    text: string;
    action: any;
    enabled: boolean;
}

interface IOwnProps {
    productId: string;
    onEntriesRefresh: () => void;
    onCloseSidePanel: () => void;
    onCloseModal: () => void;
}

const ProductEditKabob = ({ productId, onCloseModal, onEntriesRefresh, onCloseSidePanel }: IOwnProps) => {
    const classes = useStyles({});

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [isOpenedArchiveModal, setIsOpenedArchiveModal] = React.useState(false);
    const [isOpenedCancelModal, setIsOpenedCancelModal] = React.useState(false);
    const menu = React.useMemo(
        () => ({
            parent: {
                marginTop: "25px",
                zIndex: 1500,
                left: -50,
            },
            item: {
                fontFamily: "Lato",
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0.18px",
                color: themeOptions.palette.darkGrey,
            },
        }),
        [],
    );

    const handleOpenCancelProductModal = React.useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpenedArchiveModal(false);
        setIsOpenedCancelModal(true);
    }, []);
    const handleOpenArchiveProductModal = React.useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpenedArchiveModal(true);
        setIsOpenedCancelModal(false);
    }, []);
    const rawContextOptions: IContextOption[] = React.useMemo(
        () => [
            {
                key: ProductActions.Cancel,
                text: format("Cancel"),
                action: handleOpenCancelProductModal,
                enabled: true,
            },
            {
                key: ProductActions.Archive,
                text: format("Archive"),
                action: handleOpenArchiveProductModal,
                enabled: me.hasPermission("archive_product"),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const contextOptions = React.useMemo(
        () => rawContextOptions.filter((option) => option.enabled),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rawContextOptions, me],
    );

    const handleContextTrigger = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.preventDefault();
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
        },
        [setAnchorEl],
    );

    const handleContextClose = React.useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            setAnchorEl(null);
        },
        [setAnchorEl],
    );

    const handleContextAction = React.useCallback(
        (action: any) => (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
            handleContextClose(e);
            if (action) {
                action(e);
            }
        },
        [handleContextClose],
    );

    const handleCloseModal = () => {
        setIsOpenedArchiveModal(false);
        setIsOpenedCancelModal(false);
    };
    const handleOnConfirm = React.useCallback(() => {
        onCloseModal();
        onCloseSidePanel();
    }, [setAnchorEl, onCloseSidePanel]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className={classnames(classes.container)}>
            <div className={classes.contextTrigger}>
                <IconButton onClick={handleContextTrigger}>
                    <MoreHorizIcon style={{ color: themeOptions.palette.darkGrey }} />
                </IconButton>
                <Menu
                    style={menu.parent}
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleContextClose}
                >
                    {contextOptions.map(({ key, text, action }) => (
                        <MenuItem style={menu.item} key={key} onClick={handleContextAction(action)}>
                            {text}
                        </MenuItem>
                    ))}
                </Menu>
            </div>
            {isOpenedArchiveModal && (
                <ArchiveProductConfirmation
                    open={isOpenedArchiveModal}
                    productId={productId}
                    onClose={handleCloseModal}
                    onConfirm={handleOnConfirm}
                    onEntriesRefresh={onEntriesRefresh}
                />
            )}
            {isOpenedCancelModal && (
                <CancelProductConfirmation
                    open={isOpenedCancelModal}
                    productId={productId}
                    onClose={handleCloseModal}
                    onConfirm={handleOnConfirm}
                    onEntriesRefresh={onEntriesRefresh}
                />
            )}
        </div>
    );
};

export default ProductEditKabob;
